import React, { useCallback, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LocalStorageKeys, LocalStorageService } from "../utils/LocalStorageService";
import Roles from "../utils/roles";

const RoleRouteAccess = Object.freeze({
  [Roles.LUNG_ANALYST]: {
    allowed: ["/", "/Lung"],
    fallback: "/login",
  },
});


const withAuthGuard = (Component, { isPublic = false } = {}) => {
  return function Guarded(props) {
    const location = useLocation();


    const getToken = useCallback(() => {
      return LocalStorageService.get(LocalStorageKeys.USER_TOKEN);
    }, []);

    const isSessionExpired = useCallback(() => {
      const expiration = LocalStorageService.get(LocalStorageKeys.SESSION_EXPIRATION);
      return expiration && Date.now() > Number(expiration);
    }, []);

    const getUserRole = useCallback(() => {
      return LocalStorageService.get(LocalStorageKeys.USER_DETAILS)?.role;
    }, []);

    const getHomeRouteByRole = useCallback((role) => {
      switch (role) {
        case Roles.DOCTOR_ANALYST:
        case Roles.ANALYST:
        case Roles.GUEST_ANALYST:
        case Roles.PATHOLOGY_ANALYST:
        case Roles.LUNG_ANALYST:
          return "/";
        default:
          return "/login";
      }
    }, []);


    const token = useMemo(() => getToken(), [getToken]);

    const sessionExpired = useMemo(
      () => (token ? isSessionExpired() : false),
      [token, isSessionExpired]
    );

    const userRole = useMemo(() => {
      return token ? getUserRole() : null;
    }, [token, getUserRole]);

    const homeRoute = useMemo(() => {
      return userRole ? getHomeRouteByRole(userRole) : "/login";
    }, [userRole, getHomeRouteByRole]);

    const routeAccess = useMemo(() => {
      return userRole ? RoleRouteAccess[userRole] : null;
    }, [userRole]);



    if (token && sessionExpired) {
      LocalStorageService.clear();
      return <Navigate to="/login" replace />;
    }

    if (!isPublic && !token) {
      return <Navigate to="/login" replace />;
    }


    if (
      !isPublic &&
      token &&
      routeAccess &&
      !routeAccess.allowed.includes(location.pathname) 
    ) {
      LocalStorageService.clear();
      return <Navigate to={routeAccess.fallback} replace />;
    }

    if (isPublic && token) {
      return <Navigate to={homeRoute} replace />;
    }

    return <Component {...props} />;
  };
};

export default withAuthGuard;
